/**
 * The inbox, folded by who each conversation is with.
 *
 * NOT the same thing as a mail thread, and the word is deliberately not reused
 * on this screen. A thread here is already a conversation - the protocol
 * threads it, because a reply carries the id of what it answers - and a row in
 * the list is one of those. This folds a level ABOVE that: every conversation
 * with one company onto one line, however many separate subjects they span.
 * The call log's version of this is grouping by phone number, and it is the
 * same idea with the only identity that screen has.
 *
 * WHAT COUNTS AS THE SAME CORRESPONDENT, strongest identity first:
 *
 *   the linked client   the CRM's own record. Two addresses at one company are
 *                       one correspondent, which is the whole point of linking.
 *   the matched company the register code the matcher found. An identity, not a
 *                       name: two spellings of one company share a code.
 *   the address         lowercased, and THEIRS rather than a fixed column - see
 *                       `threadAddress`, which is why the Sent folder does not
 *                       fold onto our own mailbox.
 *
 * A row with none of the three keeps a key of its own and draws as the plain
 * row it always was. Grouping unidentifiable rows together would be a claim
 * that they came from one person.
 *
 * TWO ROWS THAT ARE THE SAME COMPANY CAN STILL MISS EACH OTHER, when one is
 * linked to a client and the other only carries a register code. That is
 * honest: the browser has no lookup that joins them, and guessing would fold
 * two companies together, which is the one failure worse than not folding.
 *
 * Order is the order the list arrived in. A group takes the position of its
 * newest conversation, and the conversations inside keep their order, so the
 * folder does not reshuffle when the toggle goes on.
 *
 * Pure on purpose: no React and no component import.
 */
import type { MailThread } from "../../../../lib/mail";
import { threadAddress } from "../who";

export interface MailGroup {
  /** React key, and the id the open/closed set holds. */
  key: string;
  /** Their address, lowercased, "" when the row carries none. */
  address: string;
  /** Newest first, in the order the list handed them over. Never empty. */
  rows: MailThread[];
}

/** What the folded line has to say for itself while the group is shut. */
export interface MailGroupSummary {
  /** The newest conversation - what the flat list would have drawn here. */
  latest: MailThread;
  /** Conversations nobody has read. The number that decides whether to open. */
  unread: number;
  /** Messages across the whole group, not conversations. */
  messages: number;
  /** Who the group is with. See `groupWho` - NOT `threadWho`. */
  who: string;
}

/** The identity two rows have to share to be one correspondent. */
function keyOf(th: MailThread): string {
  if (th.clientId) return `client:${th.clientId}`;
  if (th.matchedRegCode) return `reg:${th.matchedRegCode}`;
  const address = threadAddress(th);
  if (address) return `addr:${address}`;
  // Nothing to be identified by. Its own key, so it lands in a group of one.
  return `thread:${th.id}`;
}

/** Fold a page of conversations by who they are with. */
export function groupThreadsBySender(rows: MailThread[]): MailGroup[] {
  const groups: MailGroup[] = [];
  const byKey = new Map<string, MailGroup>();

  for (const th of rows) {
    const key = keyOf(th);
    const found = byKey.get(key);
    if (found) {
      found.rows.push(th);
      continue;
    }
    const group: MailGroup = { key, address: threadAddress(th), rows: [th] };
    byKey.set(key, group);
    groups.push(group);
  }

  return groups;
}

/**
 * Which group holds this conversation, or null.
 *
 * The list needs it to open the group an opened conversation is folded into. A
 * reading pane beside a list the conversation is nowhere in reads as the list
 * having lost it, and the route carries the open thread.
 */
export function groupKeyOf(groups: MailGroup[], threadId: string | null): string | null {
  if (!threadId) return null;
  for (const group of groups) {
    if (group.rows.some((th) => th.id === threadId)) return group.key;
  }
  return null;
}

/**
 * The facts the folded line prints, worked out once.
 *
 * The message count comes from each row's own `_count`, which is what the list
 * already shows a conversation is worth; a row whose count did not travel is
 * worth at least the one message the list carries.
 */
export function summarizeMailGroup(group: MailGroup): MailGroupSummary {
  // Never empty: `groupThreadsBySender` creates a group from a row, so the
  // first one is always there.
  const latest = group.rows[0]!;
  let unread = 0;
  let messages = 0;
  for (const th of group.rows) {
    if (th.unread) unread += 1;
    messages += th._count?.messages ?? 1;
  }
  return { latest, unread, messages, who: groupWho(group) };
}

/**
 * Who the folded line is with. NOT `threadWho`, on purpose.
 *
 * `threadWho` names a ROW, and its last resort is the sender's own name, which
 * on a conversation we started is OUR name. On a flat row that is a quirk; on a
 * folded line it would be a lie, because the line stands for a group we built
 * out of who the conversations are WITH. A Sent-only group would be headed by
 * our own mailbox while holding four different customers.
 *
 * So the name comes from the identities that do not depend on direction first -
 * the matched company, then the linked client, taken from whichever row in the
 * group carries one - then from the newest INBOUND message's sender name, and
 * only then from the bare address the group was keyed on.
 */
function groupWho(group: MailGroup): string {
  for (const th of group.rows) {
    if (th.matchedCompanyName) return th.matchedCompanyName;
  }
  for (const th of group.rows) {
    if (th.client?.name) return th.client.name;
  }
  for (const th of group.rows) {
    const last = th.messages?.[0];
    if (last?.direction === "inbound" && last.fromName) return last.fromName;
  }
  return group.address || "-";
}

/**
 * What the folded line's right-click menu offers, read off its conversations.
 *
 * Each toggle is decided the way the flat row decides it for one conversation,
 * stretched over the group: "mark all read" while ANY is unread, because that
 * is the badge the operator is looking at; "restore" and "not spam" only when
 * EVERY conversation is already there, because a group that is half archived
 * is still a group with something left to archive.
 */
export interface MailGroupMenu {
  ids: string[];
  anyUnread: boolean;
  allArchived: boolean;
  allSpam: boolean;
}

export function groupMenuState(group: MailGroup): MailGroupMenu {
  return {
    ids: group.rows.map((th) => th.id),
    anyUnread: group.rows.some((th) => th.unread),
    allArchived: group.rows.every((th) => th.status === "archived"),
    allSpam: group.rows.every((th) => th.status === "spam"),
  };
}
